import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CalendarClock, Eye, Loader2, Video, XCircle } from "lucide-react";
import moment from "moment";
import TableWithSearch from "@/components/TableWithSearch";
import MyDialogContent from "@/components/MyDialogContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/use-auth";
import { useOrg } from "@/contexts/useOrg";
import AxiosInstance from "@/utils/AxiosInstance";
import { getCurrentMonthYear, getErrToast, getPromise, getPromiseToast } from "@/utils/helpers";
import type { PatientV2 } from "@/components/patient-v2/types";
import { InfiniteScrollSingleSelect } from "@/components/filters/InfiniteScrollSingleSelect";
import { ScheduleTelehealthDialog } from "@/components/patient-v2/tcm/ScheduleTelehealthDialog";
import { TcmAppointmentReasonDialog } from "@/components/patient-v2/tcm/TcmAppointmentReasonDialog";
import { DashboardLayout } from "./Dashboard";

const MONGO_ID = /^[a-f0-9]{24}$/i;

const STATUS_FILTER = {
  label: "Status",
  key: "status",
  isMulti: false,
  values: [
    { label: "Scheduled", key: "scheduled" },
    { label: "Completed", key: "completed" },
    { label: "Cancelled", key: "cancelled" },
  ],
};

const STAFF_HEADERS = [
  { id: "appointmentId", label: "Appointment ID", isSortable: false },
  { id: "patient", label: "Patient Name & ID", isSortable: false },
  { id: "provider", label: "Attending Provider", isSortable: false },
  { id: "scheduledBy", label: "Scheduled By", isSortable: false },
  { id: "dateTime", label: "Date & Time", isSortable: false },
  { id: "videoRoom", label: "Video Room", isSortable: false },
  { id: "visitStatus", label: "Status", isSortable: false },
];

const PROVIDER_HEADERS = [
  { id: "patient", label: "Patient Name & ID", isSortable: false },
  { id: "scheduledBy", label: "Scheduled By", isSortable: false },
  { id: "dateTime", label: "Date & Time", isSortable: false },
  { id: "videoRoom", label: "Video Room", isSortable: false },
  { id: "visitStatus", label: "Status", isSortable: false },
];

const PATIENT_HEADERS = [
  { id: "provider", label: "Attending Provider", isSortable: false },
  { id: "scheduledBy", label: "Scheduled By", isSortable: false },
  { id: "dateTime", label: "Date & Time", isSortable: false },
  { id: "videoRoom", label: "Video Room", isSortable: false },
  { id: "visitStatus", label: "Status", isSortable: false },
];

type VisitView = "staff" | "provider" | "patient";
type StartField = "patientStartAt" | "providerStartAt";

function startFieldFor(view: VisitView): StartField {
  return view === "provider" ? "providerStartAt" : "patientStartAt";
}

function text(value: unknown) {
  return String(value ?? "").trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function personName(value: unknown) {
  if (!value || typeof value === "string") return text(value);
  if (typeof value !== "object") return "";
  const row = value as Record<string, unknown>;
  const name = [row.firstName, row.middleName, row.lastName].map(text).filter(Boolean).join(" ");
  return name || text(row.name ?? row.fullName);
}

function specialtyOf(value: unknown) {
  const row = asRecord(value);
  if (!row) return "";
  if (Array.isArray(row.specializations)) return row.specializations.map(text).filter(Boolean).join(", ");
  return text(row.specializations ?? row.specialty);
}

function businessId(row: Record<string, unknown>) {
  const candidates = [row.appointmentId, row.appointmentCode, row.aptId, row.code, row.displayId];
  for (const candidate of candidates) {
    const value = text(candidate);
    if (value && !MONGO_ID.test(value)) return value;
  }
  return text(row.appointmentId ?? row._id ?? row.id) || "—";
}

function patientRecord(row: Record<string, unknown>) {
  return asRecord(row.patient) ?? asRecord(row.patientId);
}

function patientCode(row: Record<string, unknown>, patient: Record<string, unknown> | null) {
  const code = text(patient?.patientId ?? row.patientCode);
  if (code && !MONGO_ID.test(code)) return code;
  return "";
}

function ageLabel(patient: Record<string, unknown> | null) {
  const raw = text(patient?.dob ?? patient?.dateOfBirth ?? patient?.birthDate);
  if (!raw) return "";
  const dob = new Date(raw);
  if (Number.isNaN(dob.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDelta = now.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age >= 0 ? `${age}y` : "";
}

function sexLabel(patient: Record<string, unknown> | null) {
  const value = text(patient?.sex ?? patient?.gender).toLowerCase();
  if (value === "m" || value === "male") return "M";
  if (value === "f" || value === "female") return "F";
  return value ? value.charAt(0).toUpperCase() : "";
}

function visitStart(row: Record<string, unknown>, field: StartField) {
  const iso = text(row[field]);
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.local().toDate() : null;
}

function visitLabel(start: Date | null) {
  if (!start) return "";
  return moment(start).format("MM-DD-YYYY, hh:mm A");
}

function startsIn(start: Date) {
  const minutes = Math.round((start.getTime() - Date.now()) / 60000);
  if (minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours <= 0) return `Starts in ${rest}m`;
  return `Starts in ${hours}h ${rest}m`;
}

function roomUrlFrom(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data);
  const candidates = [payload, root, root?.data, data?.appointment, data?.telehealth, data?.link, root?.appointment];
  for (const candidate of candidates) {
    const row = asRecord(candidate);
    const roomUrl = text(row?.roomUrl);
    if (roomUrl) return roomUrl;
  }
  return "";
}

function complexityOf(row: Record<string, unknown>) {
  const plan = asRecord(row.tcmPlan) ?? asRecord(row.plan);
  const value = text(row.complexity ?? plan?.complexity);
  if (!value) return "—";
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusKey(status: string) {
  return status.toLowerCase();
}

function statusClass(status: string) {
  const key = statusKey(status);
  if (key === "scheduled" || key === "pending") return "border-transparent bg-sky-100 text-sky-700";
  if (key === "completed") return "border-transparent bg-emerald-100 text-emerald-700";
  if (key === "cancelled" || key === "canceled") return "border-transparent bg-slate-100 text-slate-500";
  return "border-transparent bg-slate-100 text-slate-600";
}

function statusLabel(status: string) {
  if (!status) return "—";
  return status.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function listFrom(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  if (!record) return null;
  const nested = [
    record.appointments,
    record.telehealth,
    record.telehealthAppointments,
    record.items,
    record.results,
    record.data,
  ].find(Array.isArray);
  if (Array.isArray(nested)) return nested;
  if (record._id || record.id || record.appointmentDate || record.timeSlot) return [record];
  return null;
}

function extractRows(payload: unknown) {
  const root = asRecord(payload);
  const buckets = [payload, root?.data, root?.appointments, root?.telehealth];
  for (const bucket of buckets) {
    const list = listFrom(bucket);
    if (list) return list;
  }
  return [];
}

function mapPatientPage(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root;
  const list = Array.isArray(data?.patients)
    ? data.patients
    : Array.isArray(data?.items)
      ? data.items
      : [];
  const options = list.flatMap((item) => {
    const row = asRecord(item);
    const value = text(row?._id ?? row?.id);
    if (!value) return [];
    const name = personName(row);
    const code = text(row?.patientId);
    const label = code && !MONGO_ID.test(code) ? `${name || "Patient"} (${code})` : name || "Patient";
    return [{ value, label }];
  });
  const rawTotal = data?.total ?? asRecord(data?.pagination)?.total ?? asRecord(data?.pagination)?.count;
  const total = Number(rawTotal);
  return { options, total: Number.isFinite(total) ? total : undefined };
}

function mapProviderPage(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root;
  const list = Array.isArray(data?.providers)
    ? data.providers
    : Array.isArray(data?.items)
      ? data.items
      : [];
  const options = list.flatMap((item) => {
    const row = asRecord(item);
    const value = text(row?._id ?? row?.id);
    if (!value) return [];
    return [{ value, label: personName(row) || "Provider" }];
  });
  const rawTotal = data?.total ?? asRecord(data?.pagination)?.total ?? asRecord(data?.pagination)?.count;
  const total = Number(rawTotal);
  return { options, total: Number.isFinite(total) ? total : undefined };
}

function extractTotal(payload: unknown, page: number, limit: number, rowCount: number) {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root;
  const raw = data?.total ?? asRecord(data?.pagination)?.total ?? asRecord(data?.pagination)?.count;
  const total = Number(raw);
  if (Number.isFinite(total)) return total;
  if (data?.hasMore === true) return page * limit + 1;
  return (page - 1) * limit + rowCount;
}

function unwrapAppointment(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data);
  return asRecord(data?.appointment) ?? asRecord(root?.appointment) ?? data ?? root;
}

function VideoRoomCell({ status, onJoin }: { status: string; onJoin: () => void }) {
  if (statusKey(status) !== "scheduled") {
    return <span className="text-sm text-muted-foreground">{statusLabel(status)}</span>;
  }

  return (
    <Button type="button" size="sm" className="h-8 rounded-lg" title="Join video call" onClick={onJoin}>
      <Video className="h-3.5 w-3.5" />
      Join
    </Button>
  );
}

function displayYmd(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${match[2]}-${match[3]}-${match[1]}`;
}

function AppointmentDetailsDialog({
  appointmentId,
  onOpenChange,
}: {
  appointmentId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    AxiosInstance()
      .get(`/telehealth/appointments/${appointmentId}`)
      .then(({ data }) => {
        if (!cancelled) setDetail(unwrapAppointment(data));
      })
      .catch((error) => {
        if (!cancelled) getErrToast(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  const view = useMemo(() => {
    if (!detail) return null;
    const patient = patientRecord(detail);
    const provider = asRecord(detail.provider) ?? asRecord(detail.hostProvider);
    const providerCode = text(provider?.providerId);
    const extras = (
      [
        ["Cancel reason", text(detail.cancelReason)],
        ["Reschedule reason", text(detail.rescheduleReason)],
        ["Completion note", text(detail.completionNote)],
        ["Recording", text(detail.recordingUrl)],
        ["Transcript", text(detail.transcript)],
      ] as Array<[string, string]>
    ).filter(([, value]) => value);
    return {
      status: text(detail.status),
      patient: personName(patient) || personName(detail.patientName) || "Patient",
      patientCode: patientCode(detail, patient),
      provider: personName(provider) || "—",
      providerCode: providerCode && !MONGO_ID.test(providerCode) ? providerCode : "",
      scheduledBy: personName(detail.createdBy ?? detail.scheduledBy ?? detail.caretaker) || "—",
      appointmentDate: displayYmd(text(detail.appointmentDate)) || "—",
      timeSlot: text(detail.timeSlot) || "—",
      patientStart: text(detail.patientStartAt) || "—",
      providerStart: text(detail.providerStartAt) || "—",
      complexity: statusLabel(text(detail.tcmComplexity)) || complexityOf(detail),
      notes: text(detail.visitNotes ?? detail.note ?? detail.notes) || "—",
      joinWindow: statusLabel(text(detail.joinWindowState)),
      sendNotification: detail.sendNotification === false ? "No" : "Yes",
      extras,
    };
  }, [detail]);

  const scheduleFacts = view
    ? [
        ["Appointment date", view.appointmentDate],
        ["Time slot", view.timeSlot],
        ["Patient start", view.patientStart],
        ["Provider start", view.providerStart],
      ]
    : [];

  return (
    <Dialog open={!!appointmentId} onOpenChange={onOpenChange}>
      <MyDialogContent heading="Telehealth appointment">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !view ? (
          <p className="text-sm text-muted-foreground">Unable to load this appointment.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-[#111827]">{view.patient}</p>
                {view.patientCode ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">{view.patientCode}</p>
                ) : null}
              </div>
              <Badge className={`teachers shrink-0 rounded-full px-3 py-1 font-medium ${statusClass(view.status)}`}>
                {statusLabel(view.status)}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {scheduleFacts.map(([label, value]) => (
                <div key={label} className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 text-sm font-medium text-[#111827]">{value}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Attending provider</p>
                <p className="mt-1 text-sm font-medium text-[#111827]">{view.provider}</p>
                {view.providerCode ? (
                  <p className="text-xs text-muted-foreground">{view.providerCode}</p>
                ) : null}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Scheduled by</p>
                <p className="mt-1 text-sm font-medium text-[#111827]">{view.scheduledBy}</p>
              </div>
            </div>

            <div className="rounded-lg border border-[#E5E7EB] px-3 py-2.5">
              <p className="text-xs text-muted-foreground">Visit notes</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-[#111827]">{view.notes}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                ["Complexity", view.complexity],
                ["Join window", view.joinWindow],
                ["Notification", view.sendNotification],
              ].map(([label, value]) => (
                <span
                  key={label}
                  className="teachers rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs text-[#374151]"
                >
                  <span className="text-muted-foreground">{label}:</span> {value}
                </span>
              ))}
            </div>

            {view.extras.length > 0 ? (
              <div className="space-y-2 border-t border-[#E5E7EB] pt-3">
                {view.extras.map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="mt-0.5 break-all text-sm text-[#111827]">{value}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </MyDialogContent>
    </Dialog>
  );
}

export default function TelehealthVisits() {
  const navigate = useNavigate();
  const { user, profile, displayName } = useAuth();
  const { selectedOrgId, isAllSelected } = useOrg();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({
    date: getCurrentMonthYear(),
  });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [appointmentAction, setAppointmentAction] = useState<{
    type: "cancel" | "reschedule";
    id: string;
    patientId: string;
    tcmPlanId: string;
    patient: PatientV2 | null;
  } | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<{
    appointmentId: string;
    patientId: string;
    tcmPlanId: string;
    patient: PatientV2 | null;
    reason: string;
  } | null>(null);

  const role = text(user?.role).toLowerCase();
  const view: VisitView = role === "patient" ? "patient" : role === "provider" ? "provider" : "staff";
  const patientId = view === "patient" ? text(profile?._id ?? profile?.id) : "";
  const providerId = view === "provider" ? text(user?.providerId ?? profile?._id ?? profile?.id) : "";
  const organizationId =
    !isAllSelected && selectedOrgId && selectedOrgId !== "anthro-care" ? selectedOrgId : "";
  const ready = view === "patient" ? !!patientId : view === "provider" ? !!providerId : true;

  const filtersOptions = useMemo(() => {
    const month = { label: "Month", key: "date", type: "monthYear" as const };
    if (view === "staff") return [month];
    if (view === "provider") return [month, STATUS_FILTER];
    return [month, STATUS_FILTER];
  }, [view]);

  useEffect(() => {
    setFilters((current) => {
      if (!current.hostProviderId && !current.patientId) return current;
      return { ...current, hostProviderId: "", patientId: "" };
    });
  }, [organizationId]);

  const headers = view === "provider" ? PROVIDER_HEADERS : view === "patient" ? PATIENT_HEADERS : STAFF_HEADERS;
  const searchPlaceholder =
    view === "provider"
      ? "Search by patient name"
      : view === "patient"
        ? "Search visits by provider or topic..."
        : "Search by appointment ID, patient...";

  return (
    <DashboardLayout activePage="/telehealth">
      <div className="space-y-4">
        {/* <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">
              {view === "provider"
                ? "My Telehealth Schedule"
                : view === "patient"
                  ? "My Telehealth Visits"
                  : "Telehealth Encounters"}
            </h1>
            {view === "provider" && displayName ? (
              <Badge variant="outline" className="rounded-full px-3 py-1 font-medium">
                {displayName}
              </Badge>
            ) : null}
          </div>
          {view === "patient" ? (
            <p className="text-sm text-muted-foreground">
              View and join your upcoming virtual consultations with your care team
            </p>
          ) : null}
        </div> */}
        {ready ? (
          <TableWithSearch
            key={`${view}-${organizationId || "all"}-${patientId || providerId || "org"}`}
            columnPreferenceKey={`telehealth-visits-${view}`}
            universalLoadingSkeleton
            fetchCondition={ready}
            heading={view === "staff" ? "Telehealth Encounters" : "My Telehealth Visits"}
            isIncludeSearch
            apiDataKey=""
            columnHeaders={headers}
            searchPlaceholder={searchPlaceholder}
            url={
              view === "provider" ? `/telehealth/providers/${providerId}` : "/telehealth"
            }
            params={{
              ...(organizationId ? { organizationId } : {}),
              refreshTick,
            }}
            customFetch={async ({ page, limit, search, filters: tableFilters, params: tableParams }) => {
              const requestParams: Record<string, string | number> = { page, limit };
              if (tableParams?.organizationId) requestParams.organizationId = tableParams.organizationId;
              if (view === "staff" && tableFilters?.hostProviderId) {
                requestParams.hostProviderId = tableFilters.hostProviderId;
              }
              if (view === "provider" && tableFilters?.patientId) {
                requestParams.patientId = tableFilters.patientId;
              }
              if (tableFilters?.date) requestParams.date = tableFilters.date;
              if (tableFilters?.status) requestParams.status = tableFilters.status;
              if (search) requestParams.search = search;
              const path =
                view === "provider" ? `/telehealth/providers/${providerId}` : "/telehealth";
              try {
                const { data } = await AxiosInstance().get(path, { params: requestParams });
                const mapped = extractRows(data)
                  .map((raw) => {
                    const row = asRecord(raw) ?? {};
                    const patient = patientRecord(row);
                    const providerSource = row.hostProvider ?? row.provider ?? row.hostProviderId;
                    const providerName = personName(providerSource) || "—";
                    const specialty = specialtyOf(providerSource);
                    const patientName = personName(patient) || personName(row.patientName) || "—";
                    const code = patientCode(row, patient);
                    const meta = [code, [ageLabel(patient), sexLabel(patient)].filter(Boolean).join(" / ")]
                      .filter(Boolean)
                      .join(" • ");
                    const status = text(row.status);
                    const start = visitStart(row, startFieldFor(view));
                    const when = visitLabel(start);
                    const upcomingHint =
                      statusKey(status) === "scheduled" && start ? startsIn(start) : "";
                    const duration = text(row.durationMinutes ?? row.duration);
                    const hint = upcomingHint
                      ? upcomingHint
                      : statusKey(status) === "completed"
                        ? `Duration: ${duration || "30"} min`
                        : statusKey(status) === "scheduled"
                          ? "Scheduled: 30 min"
                          : "";
                    const appointmentMongoId = text(row._id ?? row.id);
                    const plan = asRecord(row.tcmPlan);
                    const patientMongoId = text(patient?._id ?? (MONGO_ID.test(text(row.patientId)) ? row.patientId : ""));
                    return {
                      _id: appointmentMongoId,
                      sortAt: start?.getTime() ?? 0,
                      appointmentStatus: status,
                      patientMongoId,
                      tcmPlanMongoId: text(row.tcmPlanId ?? plan?._id ?? plan?.id),
                      schedulePatient: patient
                        ? ({
                            ...patient,
                            _id: patientMongoId || text(patient._id),
                            organizationId: organizationId || patient.organizationId,
                          } as PatientV2)
                        : null,
                      appointmentId: <span className="font-medium text-primary">{businessId(row)}</span>,
                      patient: (
                        <div>
                          <p className="font-medium text-[#111827]">{patientName}</p>
                          {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
                        </div>
                      ),
                      provider: (
                        <div>
                          <p className="font-medium text-[#111827]">{providerName}</p>
                          {specialty ? <p className="text-xs text-muted-foreground">{specialty}</p> : null}
                        </div>
                      ),
                      scheduledBy: personName(row.scheduledBy ?? row.createdBy ?? row.caretaker) || "—",
                      complexity: complexityOf(row),
                      dateTime: (
                        <div>
                          <p>{when || "—"}</p>
                          {/* {hint ? (
                            <p className={`text-xs ${upcomingHint ? "text-emerald-600" : "text-muted-foreground"}`}>
                              {hint}
                            </p>
                          ) : null} */}
                        </div>
                      ),
                      videoRoom: (
                        <VideoRoomCell
                          status={status}
                          onJoin={() => {
                            if (!appointmentMongoId) return;
                            const { pr, resolveFn, rejectFn } = getPromise();
                            getPromiseToast(pr);
                            AxiosInstance()
                              .get(`/telehealth/appointments/${appointmentMongoId}/link`)
                              .then(({ data }) => {
                                const room = roomUrlFrom(data);
                                if (!room) {
                                  rejectFn("This visit does not have a meeting room");
                                  return;
                                }
                                resolveFn("Opening meeting room");
                                navigate("/telehealth/room", {
                                  state: {
                                    room,
                                    displayName: displayName || (patientName === "—" ? "" : patientName),
                                  },
                                });
                              })
                              .catch((error) => rejectFn(error));
                          }}
                        />
                      ),
                      visitStatus: (
                        <Badge className={`rounded-full px-3 py-1 font-medium ${statusClass(status)}`}>
                          {statusLabel(status)}
                        </Badge>
                      ),
                    };
                  })
                  .filter((row): row is NonNullable<typeof row> => Boolean(row))
                  .sort((a, b) => b.sortAt - a.sortAt);
                return {
                  data: mapped,
                  total: extractTotal(data, page, limit, mapped.length),
                };
              } catch (error) {
                getErrToast(error);
                throw error;
              }
            }}
            data={rows}
            viewData={rows}
            setData={setRows}
            isActionColumn={false}
            handleAction={() => {}}
            isColumnSelection
            headerExtraControls={
              view === "staff" ? (
                <InfiniteScrollSingleSelect
                  label="Provider"
                  value={filters.hostProviderId ?? ""}
                  onChange={(hostProviderId) =>
                    setFilters((current) => ({ ...current, hostProviderId }))
                  }
                  url="/providers"
                  params={organizationId ? { orgId: organizationId } : undefined}
                  mapPage={mapProviderPage}
                  emptyLabel="No providers found"
                />
              ) : view === "provider" ? (
                <InfiniteScrollSingleSelect
                  label="All Patients"
                  value={filters.patientId ?? ""}
                  onChange={(nextPatientId) =>
                    setFilters((current) => ({ ...current, patientId: nextPatientId }))
                  }
                  url="/patients"
                  params={organizationId ? { orgId: organizationId } : undefined}
                  mapPage={mapPatientPage}
                  emptyLabel="No patients found"
                />
              ) : null
            }
            filters={filters}
            defaultFilters={{ date: getCurrentMonthYear() }}
            clickRow={() => {}}
            filtersOptions={filtersOptions}
            setFilters={setFilters}
            moreActions={
              view === "staff"
                ? [
                    {
                      label: "View Full details",
                      icon: <Eye size={16} />,
                      onClick: (row: { _id?: string }) => {
                        if (row?._id) setDetailId(row._id);
                      },
                      isShow: (row: { _id?: string }) => !!row?._id,
                    },
                    {
                      label: "Reschedule",
                      icon: <CalendarClock size={16} />,
                      onClick: (row: {
                        _id?: string;
                        patientMongoId?: string;
                        tcmPlanMongoId?: string;
                        schedulePatient?: PatientV2 | null;
                      }) => {
                        if (!row?._id) return;
                        setAppointmentAction({
                          type: "reschedule",
                          id: row._id,
                          patientId: row.patientMongoId || "",
                          tcmPlanId: row.tcmPlanMongoId || "",
                          patient: row.schedulePatient ?? null,
                        });
                      },
                      isShow: (row: { _id?: string; appointmentStatus?: string }) =>
                        !!row?._id && statusKey(text(row.appointmentStatus)) === "scheduled",
                    },
                    {
                      label: "Cancel",
                      icon: <XCircle size={16} />,
                      onClick: (row: { _id?: string }) => {
                        if (!row?._id) return;
                        setAppointmentAction({
                          type: "cancel",
                          id: row._id,
                          patientId: "",
                          tcmPlanId: "",
                          patient: null,
                        });
                      },
                      isShow: (row: { _id?: string; appointmentStatus?: string }) =>
                        !!row?._id && statusKey(text(row.appointmentStatus)) === "scheduled",
                    },
                  ]
                : []
            }
          />
        ) : (
          <p className="text-sm text-muted-foreground">No data found</p>
        )}
        <AppointmentDetailsDialog
          appointmentId={detailId}
          onOpenChange={(open) => {
            if (!open) setDetailId(null);
          }}
        />
        <TcmAppointmentReasonDialog
          action={appointmentAction?.type ?? null}
          onOpenChange={(open) => {
            if (!open) setAppointmentAction(null);
          }}
          onConfirm={(reason) => {
            const current = appointmentAction;
            if (!current) return;
            if (current.type === "reschedule") {
              setScheduleTarget({
                appointmentId: current.id,
                patientId: current.patientId,
                tcmPlanId: current.tcmPlanId,
                patient: current.patient,
                reason,
              });
              return;
            }
            const { pr, resolveFn, rejectFn } = getPromise();
            getPromiseToast(pr);
            AxiosInstance()
              .post(`/telehealth/appointments/${current.id}/cancel`, { reason })
              .then(() => {
                resolveFn("Appointment cancelled");
                setRefreshTick((tick) => tick + 1);
              })
              .catch((error) => rejectFn(error));
          }}
        />
        <ScheduleTelehealthDialog
          open={!!scheduleTarget}
          onOpenChange={(open) => {
            if (!open) setScheduleTarget(null);
          }}
          patient={scheduleTarget?.patient ?? null}
          patientId={scheduleTarget?.patientId ?? ""}
          tcmPlanId={scheduleTarget?.tcmPlanId ?? ""}
          rescheduleAppointmentId={scheduleTarget?.appointmentId}
          rescheduleReason={scheduleTarget?.reason}
          onScheduled={() => setRefreshTick((tick) => tick + 1)}
        />
      </div>
    </DashboardLayout>
  );
}
